import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HardHat } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api-client';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

// N29.1 (plan validé le 24-09) — « Build a répondu », le marqueur DURABLE qui ouvre la question « Build doit-il avoir
// cette adresse ? » à la création d'une adresse ou d'une boîte. La synchro le pose à la première réponse de Build ;
// depuis N29.5 (01-10), un commentaire Uniticket d'une personne de la cible Build le pose aussi, que le ticket ait
// été envoyé depuis l'app ou non, à la synchro comme à l'ouverture de la fenêtre.
// Quand Build répond HORS de l'app — de vive voix, par mail : le canal informel repéré en réunion le 21-09 —, un
// ADMIN le pose ici, avec un mot obligatoire. Il n'est jamais effacé, contrairement au badge « Réponse Build » de la
// file, qu'une revalidation remet à zéro.
//
// La fenêtre de saisie passe par un PORTAIL : ce composant vit dans une GlassCard, dont le `backdrop-filter` ferait
// d'elle le bloc conteneur d'un `position: fixed` — l'overlay couvrirait la carte au lieu de l'écran (même défaut que
// DetailModal, 11-08).
export function BuildRepliedMarker({
  ticketId, buildRepliedAt, buildRepliedSource, canMark, disabled, onMarked,
}: {
  ticketId: string;
  buildRepliedAt: string | null | undefined;
  buildRepliedSource: string | null | undefined;
  /** Admins seulement — le serveur le vérifie de son côté. */
  canMark: boolean;
  disabled?: boolean;
  onMarked: (buildRepliedAt: string) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (buildRepliedAt) {
    // AUTHOR (N29.5) : une personne de la cible Build a commenté le ticket dans Uniticket, envoi depuis l'app ou non.
    const source = buildRepliedSource === 'MANUAL' || buildRepliedSource === 'BACKFILL' || buildRepliedSource === 'AUTHOR'
      ? buildRepliedSource : 'SYNC';
    return (
      <span className="inline-flex items-center gap-1 text-xs text-sky-700" title={t('tickets:validation.buildReplied.hint')}>
        <HardHat className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('tickets:validation.buildReplied.yes', {
          date: new Date(buildRepliedAt).toLocaleString(),
          source: t(`tickets:validation.buildReplied.source${source}`),
        })}
      </span>
    );
  }
  if (!canMark) return null;

  async function submit() {
    setBusy(true);
    try {
      const res = await apiFetch<{ data: { buildRepliedAt: string } }>(`/tickets/${ticketId}/build-replied`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim() }),
      });
      toast.success(t('tickets:validation.buildReplied.done'));
      setOpen(false);
      setNote('');
      onMarked(res.data.buildRepliedAt);
    } catch (err) {
      toast.error((err as Error)?.message || t('tickets:validation.buildReplied.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={t('tickets:validation.buildReplied.hint')}
        className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 underline decoration-dotted underline-offset-2 hover:text-sky-900 disabled:opacity-50"
      >
        <HardHat className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('tickets:validation.buildReplied.markBtn')}
      </button>
      {createPortal(
        <ConfirmDialog
          open={open}
          tone="sky"
          icon={<HardHat className="h-5 w-5" />}
          title={t('tickets:validation.buildReplied.markTitle')}
          message={(
            <div className="space-y-2 text-left">
              <p>{t('tickets:validation.buildReplied.markBody')}</p>
              <textarea
                className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('tickets:validation.buildReplied.notePlaceholder')}
              />
              <p className="text-[11px] text-slate-400">{t('tickets:validation.buildReplied.traced')}</p>
            </div>
          )}
          confirmLabel={t('tickets:validation.buildReplied.markConfirm')}
          cancelLabel={t('tickets:validation.buildReplied.markCancel')}
          busy={busy}
          confirmDisabled={note.trim().length < 3}
          onCancel={() => { if (!busy) setOpen(false); }}
          onConfirm={() => void submit()}
        />,
        document.body,
      )}
    </>
  );
}
